import { type FC, type ReactNode } from "react";

import { Group, Paper, Stack, Text } from "@mantine/core";

import { formatMilliUsd } from "../../../../lens-creator-payout/utils";
import { useTopPerformerAwards } from "../../../hooks/useTopPerformerAwards";
import { type TopPerformerAward } from "../../../models";
import { formatMonthDay, formatMonthYear } from "../../../utils";

import styles from "./EnrolledProgram.module.css";
import { PanelDataGuard } from "./PanelDataGuard";
import { type EnrolledPanelProps, ProgramEnrolledPanel } from "./ProgramEnrolledPanel";
import {
    AmountAwarded,
    NoAwardsYet,
    PaidOn,
    QualifiedPostersPaid,
    StatusPaid,
    TopPerformerEnrolledDescription,
    TopPerformerEnrolledTitle,
} from "./messages.generated";

interface TopPerformerEnrolledPanelProps extends EnrolledPanelProps {
    insights?: ReactNode;
}

const AwardMeta: FC<{ award: TopPerformerAward }> = ({ award }) => {
    // award.paidOn is when LCB recorded the payout, not the month above (the month the award was earned).
    const paid = award.paidOn ? <PaidOn date={formatMonthDay(award.paidOn)} /> : <StatusPaid />;
    return award.qualifiedPosters === null ? paid : <QualifiedPostersPaid count={award.qualifiedPosters} paid={paid} />;
};

export const TopPerformerEnrolledPanel: FC<TopPerformerEnrolledPanelProps> = ({
    lensId,
    unlockableId,
    monetizationUrl,
    onViewInMonetization,
    insights,
}) => {
    const { loading, error, totalEarnedMilliUsd, awards } = useTopPerformerAwards({ lensId, unlockableId });
    const hasAward = totalEarnedMilliUsd > 0 || awards.length > 0;

    return (
        <ProgramEnrolledPanel
            title={<TopPerformerEnrolledTitle />}
            description={<TopPerformerEnrolledDescription />}
            monetizationUrl={monetizationUrl}
            onViewInMonetization={onViewInMonetization}
        >
            <PanelDataGuard loading={loading} error={error}>
                {!hasAward && insights}
                <Paper className={styles.tile}>
                    <Text className={styles.tileLabel}>
                        <AmountAwarded />
                    </Text>
                    <div className="heading-h300">{formatMilliUsd(totalEarnedMilliUsd)}</div>
                    {hasAward ? (
                        <Stack className={styles.awardList}>
                            {awards.map((award) => (
                                <Group key={award.month} className={styles.awardRow}>
                                    <div>
                                        <Text className={styles.awardMonth}>{formatMonthYear(award.month)}</Text>
                                        <Text className={styles.caption}>
                                            <AwardMeta award={award} />
                                        </Text>
                                    </div>
                                    {awards.length > 1 && (
                                        <Text className={styles.awardAmount}>
                                            {formatMilliUsd(award.amountMilliUsd)}
                                        </Text>
                                    )}
                                </Group>
                            ))}
                        </Stack>
                    ) : (
                        <Text className={styles.caption}>
                            <NoAwardsYet />
                        </Text>
                    )}
                </Paper>
            </PanelDataGuard>
        </ProgramEnrolledPanel>
    );
};
